import { useEffect, useState } from "react";
import { Footprints } from "lucide-react";
import { api } from "@/lib/api";

const BAND = {
  below: { label: "Below norm", cls: "text-red-700" },
  within: { label: "Within norm", cls: "text-emerald-700" },
  above: { label: "Above norm", cls: "text-sky-700" },
};
const SYM = {
  balanced: { label: "Balanced", cls: "text-emerald-700" },
  mild: { label: "Mild asymmetry", cls: "text-amber-700" },
  significant: { label: "Significant asymmetry", cls: "text-red-700" },
};
const ordinal = (n) => `${n}${n % 100 >= 10 && n % 100 <= 20 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th")}`;
const cap = (s) => s[0].toUpperCase() + s.slice(1);

function SideRow({ side, r }) {
  return (
    <div data-testid={`knee-norms-side-${side}`}>
      {cap(side)} {Number(r.kg).toFixed(1)} kg
      {r.pct_bw != null && (
        <>
          {" "}({r.pct_bw}% BW) — <span className={`font-medium ${BAND[r.band].cls}`}>
            {BAND[r.band].label} (~{ordinal(r.percentile)} percentile, norm {r.mean} ± {r.sd}% BW)
          </span>
          {r.impaired && <span className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-2xs font-semibold text-red-800">Impaired (&lt; −2 SD)</span>}
        </>
      )}
    </div>
  );
}

// SbN knee flexion / extension vs Andrews 1996 norms (% body weight) + left/right balance + H:Q.
export function KneeNormsPanel({ assessmentId, refreshKey }) {
  const [cmp, setCmp] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/assessments/${assessmentId}/knee-norms`)
      .then((r) => { if (live) setCmp(r.data); })
      .catch(() => { if (live) setCmp(null); });
    return () => { live = false; };
  }, [assessmentId, refreshKey]);
  if (!cmp?.available) return null;
  const src = cmp.demographic_sources || {};
  return (
    <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50/60 p-3 text-xs text-zinc-700" data-testid="knee-norms">
      <div className="flex items-center gap-1.5 text-zinc-500">
        <Footprints className="h-3.5 w-3.5" />
        Knee strength vs norms
        {cmp.rated
          ? ` · ${cap(cmp.sex)}, age ${cmp.age} · band ${cmp.age_band}${cmp.nearest_band ? " (nearest band)" : ""} · body weight ${cmp.weight_kg} kg${src.weight ? ` (${src.weight})` : ""}`
          : ` · ${cmp.reason}`}
      </div>
      {Object.entries(cmp.tests).map(([key, t]) => (
        <div key={key} className="space-y-0.5" data-testid={`knee-norms-${key}`}>
          <div className="font-semibold text-zinc-900">{cap(t.label)}</div>
          {Object.entries(t.sides).map(([side, r]) => <SideRow key={side} side={side} r={r} />)}
          {t.asymmetry && (
            <div data-testid={`knee-norms-${key}-asymmetry`}>
              Left/right: {t.asymmetry.diff_pct}% difference —{" "}
              <span className={`font-medium ${SYM[t.asymmetry.band].cls}`}>{SYM[t.asymmetry.band].label}</span>
              {t.asymmetry.band !== "balanced" && ` (weaker ${t.asymmetry.weaker})`}
            </div>
          )}
        </div>
      ))}
      {cmp.hq && (
        <div className="text-zinc-600" data-testid="knee-norms-hq">
          H:Q ratio {["left", "right"].filter((s) => cmp.hq[s] != null).map((s) => `${cap(s)} ${cmp.hq[s]}`).join(", ")}
          {cmp.hq.expected && ` (expected ≈ ${cmp.hq.expected})`} — for information.
        </div>
      )}
      <div className="text-2xs text-zinc-400">{cmp.source}</div>
    </div>
  );
}
